import { DPR, POPUP_OVERLAY_DEPTH, POPUP_DEPTH } from '@constants/common.const';
import { ActionType } from '@enums/node-types.enum';
import { SceneKey } from '@models/scene-keys';
import { ApiService } from '@services/api/ApiService';
import { GameConfigService } from '@services/core/GameConfigService';
import { TranslationService } from '@services/core/TranslationService';
import { LayoutService } from '@services/ui/LayoutService';
import Phaser from 'phaser';
import {
  preloadCelebration,
  createCelebrationSprite,
  playAnimation,
} from '../ui/animations/CelebrationFx';
import { BlurBackground } from '../ui/BlurBackground';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';

export default class Finish extends Phaser.Scene {
  constructor() {
    super(SceneKey.Finish);
  }

  private layout!: LayoutService;
  private titleText!: Phaser.GameObjects.Text;
  private playButton!: Button;
  private blurBg!: BlurBackground;
  private scrim!: Phaser.GameObjects.Rectangle;
  private finishImage!: Phaser.GameObjects.Image;
  private celebrationSprite?: Phaser.GameObjects.Sprite;
  private spinner?: Spinner;

  preload() {
    preloadCelebration(this);
  }

  create() {
    const gameConfigService = new GameConfigService();
    const cfg = new GameConfigService();
    this.layout = new LayoutService(this.scale);

    // Capture and blur the Game + UI scenes underneath
    this.captureAndBlurBackground();

    // Scrim overlay (same color/alpha as modal sheet)
    const { width, height } = this.scale;
    this.scrim = this.add
      .rectangle(0, 0, width, height, 0x000c35, 0.7)
      .setOrigin(0, 0)
      .setDepth(POPUP_OVERLAY_DEPTH)
      .setScrollFactor(0)
      .setInteractive();

    const centerX = this.layout.centerX();
    const centerY = this.layout.centerY();
    const finishAction = (
      gameConfigService.currentStage!.actionsConfig?.actions || []
    ).find((action) => action.actionType === ActionType.Finish);

    this.finishImage = this.add
      .image(centerX, centerY - 60 * DPR, finishAction?.uuid ?? '__DEFAULT')
      .setDepth(POPUP_DEPTH)
      .setScrollFactor(0);

    // Scale the image to a reasonable display size
    const targetSize = 180 * DPR;
    const imgScale =
      targetSize / Math.max(this.finishImage.width, this.finishImage.height);
    this.finishImage.setScale(imgScale);

    // Celebration animation around the image
    this.celebrationSprite =
      createCelebrationSprite(this, {
        x: centerX,
        y: this.finishImage.y - this.finishImage.displayHeight / 2,
        depth: POPUP_DEPTH + 1,
        scrollFactor: 0,
      }) ?? undefined;

    if (this.celebrationSprite) {
      playAnimation(this, this.celebrationSprite, {
        repeat: -1,
        timeScale: 1.2,
      });
      this.celebrationSprite.setScale(1.3);
    }

    // text below the image
    const imageBottom = this.finishImage.y + this.finishImage.displayHeight / 2;
    const textColor =
      cfg.getColorHex('--z-result-page-text-color') ??
      cfg.getColorHex('--color-neutral-100') ??
      '#FFFFFF';
    this.titleText = this.add
      .text(centerX, imageBottom + 32 * DPR, 'You won!', {
        fontFamily: 'korolev, sans-serif',
        fontSize: `${48 * DPR}px`,
        fontStyle: '700',
        align: 'center',
        color: textColor,
        resolution: DPR,
      })
      .setOrigin(0.5)
      .setDepth(POPUP_DEPTH)
      .setScrollFactor(0);
    // Button below the text
    const api = new ApiService();
    const configService = new GameConfigService();
    const buttonY = this.titleText.y + this.titleText.height / 2 + 42 * DPR;
    // TODO: check is it's isLastChapter and change text to "Play Again" or something like that
    this.playButton = new Button({
      scene: this,
      x: centerX,
      y: buttonY,
      text: new TranslationService().translate('finish.nextChapterButton'),
      onClick: async () => {
        this.playButton.setVisible(false);

        this.spinner = new Spinner(this, centerX, buttonY, {
          color: configService.getColor('--color-accent-2'),
        });
        this.add.existing(this.spinner);
        this.spinner.setDepth(POPUP_DEPTH);
        this.spinner.setScrollFactor(0);
        this.spinner.start();

        const params = configService.sessionParams!;
        // fetch new stage url
        const gameResponse = await api.fetchGameConfigUrl(params);
        // set new stage config url
        configService.setGameResponse(gameResponse);

        this.scene.stop(SceneKey.UI);
        this.scene.stop(SceneKey.Finish);
        this.scene.start(SceneKey.Preload);
      },
    });
    this.playButton.setDepth(POPUP_DEPTH);

    // Animate entrance
    this.animateEntrance();

    // Cleanup on shutdown
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
    this.scale.on('resize', this.handleResize, this);
  }

  private captureAndBlurBackground(): void {
    this.blurBg = new BlurBackground(this, '__finish_blur_rt__');
    const gameScene = this.scene.get(SceneKey.Game);
    if (gameScene) {
      this.blurBg.capture(gameScene);
    }
  }

  private animateEntrance(): void {
    // Fade in scrim
    this.scrim.setAlpha(0);
    this.tweens.add({
      targets: this.scrim,
      alpha: 1,
      duration: 300,
      ease: 'Cubic.Out',
    });

    // Fade + scale-up the content elements
    const targets = [this.finishImage, this.titleText, this.playButton];
    const originalScales = targets.map((t) => ({ x: t.scaleX, y: t.scaleY }));

    targets.forEach((obj, i) => {
      obj.setAlpha(0);
      obj.setScale(originalScales[i].x * 0.8, originalScales[i].y * 0.8);

      this.tweens.add({
        targets: obj,
        alpha: 1,
        scaleX: originalScales[i].x,
        scaleY: originalScales[i].y,
        duration: 400,
        delay: 150,
        ease: 'Back.Out',
      });
    });
  }

  private handleResize(): void {
    if (!this.scene?.isActive()) return;

    const { width, height } = this.scale;
    const centerX = this.layout.centerX();
    const centerY = this.layout.centerY();

    this.scrim.setSize(width, height);
    this.blurBg.updateSize(width, height);
    this.finishImage.setPosition(centerX, centerY - 60 * DPR);
    this.celebrationSprite?.setPosition(centerX, this.finishImage.y);

    const imageBottom = this.finishImage.y + this.finishImage.displayHeight / 2;
    this.titleText.setPosition(centerX, imageBottom + 32 * DPR);
    this.playButton.setPosition(
      centerX,
      this.titleText.y + this.titleText.height / 2 + 42 * DPR,
    );
  }

  shutdown(): void {
    this.scale.off('resize', this.handleResize, this);
    this.blurBg.destroy();
  }
}
